<template>
  <v-container class="fill-height auth-page" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <AuthBrandHeader />

        <v-card class="auth-card" flat>
          <v-card-text class="pa-6">
            <h1 class="auth-heading">Welcome back</h1>
            <p class="auth-subtext">Log in to continue.</p>

            <!-- Renders only on apps with a Google client id configured. -->
            <GoogleAuthButton mode="login" one-tap />

            <v-form
              ref="form"
              class="auth-form-compact"
              v-model="valid"
              autocomplete="off"
              data-test="login-form"
              @submit.prevent="login"
            >
              <v-text-field
                v-model="email"
                hide-details="auto"
                :rules="emailRules"
                label="Email"
                id="login-email"
                name="login_email"
                data-test="login-email"
                prepend-icon="mdi-email"
                type="email"
                autocomplete="off"
                outlined
                dense
                required
              ></v-text-field>

              <v-text-field
                v-model="password"
                hide-details="auto"
                :rules="passwordRules"
                label="Password"
                id="login-password"
                name="login_password"
                data-test="login-password"
                prepend-icon="mdi-lock"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                autocomplete="off"
                outlined
                dense
                required
              ></v-text-field>

              <div class="text-right mt-1 mb-4">
                <router-link to="/forgot-password" class="auth-text-link">
                  Forgot password?
                </router-link>
              </div>

              <v-btn
                class="auth-submit-btn"
                type="submit"
                data-test="login-submit"
                block
                large
                :loading="loading"
              >
                Log in
              </v-btn>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-text class="auth-aux-line">
            New to {{ $config.productName }}?
            <router-link :to="registerLink" class="auth-text-link">
              Create an account
            </router-link>
          </v-card-text>
        </v-card>

        <!-- A paid-only app may name its own line ($config.PAID_TRUST_LINE). -->
        <div v-if="paidTrustLine" class="auth-trust-line">
          <template v-for="(part, index) in paidTrustLine">
            <span v-if="index" :key="'sep' + index" class="auth-trust-sep">·</span>
            <span :key="'part' + index">{{ part }}</span>
          </template>
        </div>
        <div v-else class="auth-trust-line">
          <template v-if="!forcePaidPlans">
            No credit card required
            <span class="auth-trust-sep">·</span>
            <!-- Apps override the free-tier hook via config
                 (authTrustHighlight); default is the platform's
                 starter-credit model. -->
            {{ $config.authTrustHighlight || "one-time starter credits" }}
            <span class="auth-trust-sep">·</span>
          </template>
          Cancel anytime
        </div>

        <div class="auth-footer-links">
          <a v-if="landingUrl" :href="landingUrl" target="_blank" rel="noopener"
            >About {{ $config.productName }}</a
          >
          <span v-if="landingUrl" class="auth-footer-sep">·</span>
          <router-link v-if="$config.SUPPORT_PATH" :to="$config.SUPPORT_PATH">Support</router-link>
          <a v-else :href="'mailto:' + $config.SUPPORT_EMAIL">Support</a>
        </div>

        <!-- Email Not Verified Alert -->
        <v-alert v-if="emailNotVerified" type="warning" class="mt-4">
          <div class="d-flex flex-column">
            <span>
              Your email has not been verified. Please check your inbox for the
              verification link.
            </span>
            <div class="mt-3">
              <v-btn
                small
                color="warning"
                :loading="resendingVerification"
                @click="resendVerification"
              >
                Resend Verification Email
              </v-btn>
            </div>
            <span v-if="resendSuccess" class="mt-2 text-body-2 success-text">
              Verification email sent! Please check your inbox.
            </span>
          </div>
        </v-alert>

        <!-- Error Alert -->
        <v-alert
          v-if="error && !emailNotVerified"
          type="error"
          dismissible
          data-test="login-error-alert"
          class="mt-4"
          @input="clearError"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import AuthBrandHeader from "../components/AuthBrandHeader.vue";
import GoogleAuthButton from "../components/GoogleAuthButton.vue";
import { authQuery, takeAuthNext } from "@shared/utils/memberFunnel";

export default {
  name: "LoginView",
  components: {
    AuthBrandHeader,
    GoogleAuthButton
  },
  data() {
    return {
      valid: false,
      email: "",
      password: "",
      showPassword: false,
      emailNotVerified: false,
      resendingVerification: false,
      resendSuccess: false,
      emailRules: [
        v => !!v || "Email is required",
        v => /.+@.+\..+/.test(v) || "Email must be valid"
      ],
      passwordRules: [
        v => !!v || "Password is required",
        v => v.length >= 6 || "Password must be at least 6 characters"
      ]
    };
  },
  computed: {
    ...mapGetters(["loading", "error", "forcePaidPlans"]),
    paidTrustLine() {
      var line = this.$config.PAID_TRUST_LINE;
      return this.forcePaidPlans && Array.isArray(line) && line.length ? line : null;
    },
    // Registering instead keeps the destination (?next=, ?plan=).
    registerLink() {
      return { path: "/register", query: authQuery(this.$route.query) };
    },
    landingUrl() {
      if (typeof this.$config.LANDING_PAGE_URL === 'string') return this.$config.LANDING_PAGE_URL;
      return process.env.VUE_APP_LANDING_PAGE_URL || "https://multisaas.com";
    }
  },
  methods: {
    async login() {
      // Reset states
      this.emailNotVerified = false;
      this.resendSuccess = false;

      if (this.$refs.form.validate()) {
        try {
          await this.$store.dispatch("login", {
            email: this.email,
            password: this.password
          });

          // Continue where the visitor was headed (an explicit ?next=, or a
          // destination remembered when they registered), else the dashboard.
          this.$router.push(takeAuthNext(this.$route.query) || "/").catch(() => {});
        } catch (error) {
          // Check if the error is due to unverified email
          var errorDetail = "";
          if (
            error.response &&
            error.response.data &&
            error.response.data.detail
          ) {
            errorDetail = error.response.data.detail;
          }
          if (
            errorDetail.toLowerCase().includes("email not verified") ||
            errorDetail.toLowerCase().includes("verify your email")
          ) {
            this.emailNotVerified = true;
            this.$store.commit("SET_ERROR", null);
          }
          console.error("Login failed:");
        }
      }
    },
    async resendVerification() {
      this.resendingVerification = true;
      this.resendSuccess = false;
      try {
        await this.$store.dispatch("resendVerificationEmail", this.email);
        this.resendSuccess = true;
      } catch (error) {
        console.error("Failed to resend verification:");
      } finally {
        this.resendingVerification = false;
      }
    },
    clearError() {
      this.$store.commit("SET_ERROR", null);
      this.emailNotVerified = false;
      this.resendSuccess = false;
    }
  }
};
</script>

<style scoped>
.success-text {
  color: var(--color-success-material);
}
</style>
